import { ConnectedUser } from '@ucc/admin-global-state'
import moment from 'moment'

import config from 'config'
import { Account } from 'types/Account'
import { QUALTRICS_SURVEY_TRIGGER_TYPES } from 'utils/constants'
import { queryQualtricsSurvey } from './qualtrics-script'

export interface QualtricsSurveyData {
  email?: string
  survey_trigger_event?: string
  survey_trigger_contextkey?: string
  created_at?: string | number
  product_list?: string | null
  user_amount?: number | null
  billing_type?: string
  page_ref?: string
  page_url?: string
  language?: string
  licenses?: string | null
  devices_percent_used?: string
  users_percent_used?: string
  concurrent_perfect_used?: string
  age_days?: number
  pbx_id?: string
  pbx_domain?: string
  pbx_name?: string
  pbx_creationDate?: string
  user_id?: string
  app_version?: string
  legacy_admin_center?: string
  adminCenterTransitionTime?: number
  unifiedAdmin?: boolean
}
interface GetQualtricsDataProps {
  iAm: ConnectedUser | null
  account: any
  goToAdminTime: any
}

const getLanguage = (locale) => {
  if (locale) {
    if (locale === '"zh_CN"') {
      return 'zh_CN'
    }

    const languageArray = locale.split('_')

    if (languageArray[0] === 'en') {
      return 'EN'
    } else {
      return languageArray[1]
    }
  }
  return undefined
}

const getCreatedAt = (account, createdTime) => {
  if (account.enabledDate) {
    return moment(account.enabledDate).format('X')
  } else if (account.invitedDate) {
    return moment(account.invitedDate).format('X')
  } else if (createdTime) {
    return moment(createdTime).format('X')
  }
  return 1345512787 // the default time when createTime is undefined is the date we introduced createTime
}

const getAgeInDays = (userCreationTimeStamp) =>
  Math.floor(moment.duration(moment().diff(moment(userCreationTimeStamp * 1000))).asDays())

export const storeLicensesUsage = (licenses) => {
  if (licenses) {
    const usersStats = [0, 0]
    const devicesStats = [0, 0]
    const concurrenceLicenseStats = [0, 0]
    const licenseData = licenses.results?.map((license) => {
      let licensesUsed: string
      if (license.seats) {
        if (license.devicesOnly) {
          licensesUsed = `${license.deviceCount || 0}/${license.seats || 0}`
          devicesStats[0] += license.deviceCount
          devicesStats[1] += license.seats
        } else {
          licensesUsed = `${
            (license.userCount || 0) + (license.invitedUserKeys?.length || 0) + (license.deviceKeys?.length || 0)
          }/${license.seats || 0}`
          usersStats[0] +=
            license.userCount + +(license.invitedUserKeys?.length || 0) + (license.deviceKeys?.length || 0)
          usersStats[1] += license.seats
        }
      } else {
        if (license.concurrentLicense) {
          licensesUsed = `${license.userCount || 0} (${license.concurrentLicense.userCount || 0}/${
            license.concurrentLicense.seats || 0
          })`
          concurrenceLicenseStats[0] += license.concurrentLicense.userCount
          concurrenceLicenseStats[1] += license.concurrentLicense.seats
        } else {
          licensesUsed = `${license.userCount}`
        }
      }
      return `${license.description}: ${licensesUsed}`
    })
    sessionStorage.setItem('licenses', licenseData?.sort().join(', ') || '')
    const devicesPercentage = devicesStats[1] > 0 ? ((devicesStats[0] / devicesStats[1]) * 100).toFixed(2) + '' : '-'
    const usersPercentage = usersStats[1] > 0 ? ((usersStats[0] / usersStats[1]) * 100).toFixed(2) + '' : '-'
    const concurrenceLicensePercentage =
      concurrenceLicenseStats[1] > 0
        ? ((concurrenceLicenseStats[0] / concurrenceLicenseStats[1]) * 100).toFixed(2) + ''
        : '-'
    sessionStorage.setItem('usagePercentage', `${devicesPercentage},${usersPercentage},${concurrenceLicensePercentage}`)
  }
}

export const storePbxInfoForQualtrics = (pbx) => {
  const pbx_id = pbx?.jrn?.split(':')[4] || '-'
  const pbx_domain = pbx?.domain || '-'
  const pbx_name = pbx?.name || '-'
  const pbx_creation = pbx?.creationDate || '-'
  const pbx_adminCenterTransitionTime = pbx?.adminCenterTransitionTime || ''
  sessionStorage.setItem(
    'pbxInfo',
    `${pbx_id},${pbx_domain},${pbx_name},${pbx_creation},${pbx_adminCenterTransitionTime}`,
  )
}

export const getQualtricsData = ({ iAm, account, goToAdminTime }: GetQualtricsDataProps): QualtricsSurveyData => {
  const qualtricsData: QualtricsSurveyData = {}
  const usageData = sessionStorage.getItem('usagePercentage')
  const usageDataArray = usageData?.split(',') || []
  const pbxInfo = sessionStorage.getItem('pbxInfo')
  const pbxInfoArray = pbxInfo?.split(',') || []
  let legacyAdminCenter = '-'
  let adminCenterTransitionTime = 0
  if (pbxInfoArray[4]) {
    legacyAdminCenter = 'GoToConnect Admin Center'
    adminCenterTransitionTime = parseInt(pbxInfoArray[4])
  } else if (goToAdminTime) {
    goToAdminTime = parseInt(goToAdminTime) //- 1209600000
    legacyAdminCenter = 'External Admin Center'
    adminCenterTransitionTime = parseInt(goToAdminTime)
  }

  qualtricsData.email = iAm?.email
  qualtricsData.created_at = getCreatedAt(account, iAm?.createTime)
  qualtricsData.language = getLanguage(iAm?.locale) || '-'
  qualtricsData.product_list = sessionStorage.getItem('product_list')
  qualtricsData.licenses = sessionStorage.getItem('licenses')
  qualtricsData.user_amount = parseInt(sessionStorage.getItem('usercount') || '0')
  qualtricsData.age_days = getAgeInDays(qualtricsData.created_at)
  qualtricsData.devices_percent_used = usageDataArray[0]
  qualtricsData.users_percent_used = usageDataArray[1]
  qualtricsData.concurrent_perfect_used = usageDataArray[2]
  qualtricsData.billing_type = getBillingType(account, iAm)
  qualtricsData.legacy_admin_center = legacyAdminCenter
  qualtricsData.adminCenterTransitionTime = adminCenterTransitionTime
  qualtricsData.pbx_id = pbxInfoArray[0] || '-'
  qualtricsData.pbx_domain = pbxInfoArray[1] || '-'
  qualtricsData.pbx_name = pbxInfoArray[2] || '-'
  qualtricsData.pbx_creationDate = pbxInfoArray[3] || '-'
  qualtricsData.user_id = iAm?.key || '-'
  qualtricsData.app_version = config?.version || '-'
  qualtricsData.unifiedAdmin = !!account.unifiedAdmin

  return qualtricsData
}

export const getTriggerEventType = (data) => {
  /* Call the two-week OSAT survey in review mode if:
   - the default admin center is GoTo Admin (not an admin who is previewing GoTo Admin)
   - the migration time is recorded (not a new GoTo Admin client)
   - 14 days or more time passed since the migration date
   */

  if (data.unifiedAdmin && data.adminCenterTransitionTime) {
    const timeNow = moment()
    const migrationTime = moment(parseInt(data.adminCenterTransitionTime))
    if (Math.floor(moment.duration(timeNow.diff(migrationTime)).asDays()) >= 14) {
      return QUALTRICS_SURVEY_TRIGGER_TYPES.review
    }
  }
  return QUALTRICS_SURVEY_TRIGGER_TYPES.timer
}

export const getBillingType = (account: Account, iAm: ConnectedUser | null) => {
  let billingType: string, adminInfo: string
  if (account.billingAdmin) {
    adminInfo = account.billingAdmin.key !== iAm?.key ? 'admin' : 'billingAdmin'
  } else {
    adminInfo = 'touchSales'
  }
  switch (adminInfo) {
    case 'touchSales':
      billingType = 'Sterling'
      break
    case 'billingAdmin':
      billingType = 'Billing Center - Billing Admin'
      break
    default:
      billingType = 'Billing-Center-Non-billing Admin'
      break
  }
  return billingType
}

export const qualtricsSurveyInit = (iAm: ConnectedUser, currentAccountKey, goToAdminTime) => {
  const account = iAm?.accounts?.find((acct) => acct.key === currentAccountKey)

  window.QS = {}

  window.QS.QSLoadListenerAdded = false
  window.QS.hasSurveyBeenInitialized = false

  window.QS_interceptLoaded = () => {
    window.QS.hasInterceptLoaded = !!(window.QSI && window.QSI.API)
  }
  window.QS_runIntercept = () => {
    if (
      window.QS.hasInterceptLoaded &&
      (!sessionStorage.getItem('qualtricsSurveyWasCalled') ||
        window.QS.survey_trigger_event === QUALTRICS_SURVEY_TRIGGER_TYPES.switchback)
    ) {
      console.log('Qualtrics survey data: ', window.QS) //Please do not remove this console.log() statement, but comment it out before code release. The uncommented statement in this place is very useful for development during debugging and testing. It displays the values sent to Qualtrics, when the calls are sent, and which event caused the request to be sent to Qualtrics for survey display.
      sessionStorage.setItem('qualtricsSurveyWasCalled', 'true')
      window.QSI.API.run()
    }
  }

  window.QS_triggerIntercept = () => {
    if (window.QS.hasInterceptLoaded) {
      window.QSI.API.unload()
      window.QSI.API.load().done(window.QS_runIntercept)
    }
  }

  window.startupQualtricsSurvey = () => {
    if (window.QS.QSLoadListenerAdded) {
      return true
    }
    if (!window.QS.QSLoadListenerAdded) {
      window.addEventListener('qsi_js_loaded', window.QS_interceptLoaded, false)
      window.QS.QSLoadListenerAdded = true
    }
    window.QS.CollectDataAndCallSurvey = () => {
      const data = getQualtricsData({ iAm, account, goToAdminTime })
      // don't trigger the survey more than once per session - except when switching back
      if (
        sessionStorage.getItem('qualtricsSurveyWasCalled') &&
        window.QS.survey_trigger_event != QUALTRICS_SURVEY_TRIGGER_TYPES.switchback
      ) {
        return
      }
      const path = window.location.pathname.split('/').filter((item) => item && isNaN(parseInt(item)))
      window.QS.page_url = path.join('/')
      window.QS.page_ref = document.title
      window.QS.licenses = data.licenses
      window.QS.product_list = data.product_list
      window.QS.users_amount = data.user_amount
      window.QS.devices_percent_used = data.devices_percent_used
      window.QS.users_percent_used = data.users_percent_used
      window.QS.concurrent_percent_used = data.concurrent_perfect_used
      window.QS.email = data.email
      window.QS.emaildomain = data.email?.split('@')[1]
      window.QS.created_at = data.created_at
      window.QS.age_days = data.age_days
      window.QS.language = data.language
      window.QS.legacy_admin_center = data.legacy_admin_center
      window.QS.goToAdmin_transition_time = data.adminCenterTransitionTime
        ? moment(data.adminCenterTransitionTime).format('dddd, MMMM Do YYYY, h:mm:ss A')
        : '-'
      if (!window.QS.survey_trigger_event) {
        window.QS.survey_trigger_event = getTriggerEventType(data)
        window.QS.survey_trigger_contextKey = ''
      }
      window.QS.billing_type = data.billing_type
      window.QS.survey_source = 'GoTo Admin'
      window.QS.app_version = data.app_version
      window.QS.pbx_id = data.pbx_id
      window.QS.pbx_domain = data.pbx_domain
      window.QS.pbx_name = data.pbx_name
      window.QS.pbx_creationDate = data.pbx_creationDate
      window.QS.user_id = data.user_id

      window.QS.hasSurveyBeenInitialized = true
      window.QS_triggerIntercept()
    }

    if (!window.QSI) {
      const qId = config.QUALTRICS_GOTOADMIN_CES_OSAT_SURVEY_ID
      const qURL = config.QUALTRICS_GOTOADMIN_CES_OSAT_SURVEY_URL
      queryQualtricsSurvey(qId, qURL)
      // show the survey after thirty seconds past login - timer-based trigger should start with no trigger event.
      setTimeout(function () {
        if (
          window.QS.hasInterceptLoaded &&
          !sessionStorage.getItem('qualtricsSurveyWasCalled') &&
          !window.QS.hasSurveyBeenInitialized
        ) {
          window.QS.survey_trigger_event = ''
          window.QS.CollectDataAndCallSurvey()
        }
      }, 30000) // 30 seconds
    }
  }
}

export function triggerQualtricsSurvey(surveyType, activity): any {
  if (window.QS && window.QS.hasInterceptLoaded) {
    window.QS.survey_trigger_event = surveyType
    window.QS.survey_trigger_contextKey = ''
    if (surveyType === QUALTRICS_SURVEY_TRIGGER_TYPES.success || surveyType === QUALTRICS_SURVEY_TRIGGER_TYPES.error) {
      let hasTwoConsecutiveErrors = false
      window.QS.survey_trigger_contextKey = `JobStatus: ${activity.status} - JobType: ${activity.type} - JobId: ${activity.id} - Message: ${activity.message}`

      if (surveyType === QUALTRICS_SURVEY_TRIGGER_TYPES.success) {
        sessionStorage.setItem('hasTwoConsecutiveErrors', 'false')
      } else {
        const sessionVal = sessionStorage.getItem('hasTwoConsecutiveErrors')
        hasTwoConsecutiveErrors = sessionVal ? sessionVal === 'true' : false
        if (!hasTwoConsecutiveErrors) {
          sessionStorage.setItem('hasTwoConsecutiveErrors', 'true')
        }
      }

      if (surveyType === QUALTRICS_SURVEY_TRIGGER_TYPES.success || hasTwoConsecutiveErrors) {
        window.QS.CollectDataAndCallSurvey()
        sessionStorage.removeItem('hasTwoConsecutiveErrors')
      }
    } else {
      window.QS.CollectDataAndCallSurvey()
    }
  }
  return true
}
